import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Crown, Trophy, Target, Users, SquaresFour, UserCircle, Shield, List, X, Globe, Skull, SoccerBall } from '@phosphor-icons/react';
import { useState } from 'react';

const Navigation = () => {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'pt' : 'en';
    i18n.changeLanguage(newLang);
  };

  const navItems = [
    { path: '/dashboard', label: t('nav.dashboard'), icon: SquaresFour },
    { path: '/predictions', label: t('nav.predictions'), icon: Target, comingSoon: true },
    { path: '/world-cup', label: t('nav.worldCup'), icon: Globe },
    { path: '/wc-match-predictions', label: t('nav.wcMatchPredictions'), icon: SoccerBall },
    { path: '/wc-lms', label: t('nav.wcSurvivor'), icon: Skull },
    { path: '/last-man-standing', label: t('nav.lastManStanding'), icon: Trophy, comingSoon: true },
    { path: '/leagues', label: t('nav.leagues'), icon: Users },
  ];

  if (user?.role === 'admin') {
    navItems.push({ path: '/admin', label: t('nav.admin'), icon: Shield });
  }

  return (
    <nav className="glass-effect sticky top-0 z-50 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16">
          <Link to="/dashboard" className="flex items-center space-x-3" data-testid="nav-logo">
            <div className="relative w-12 h-12 bg-black border-2 border-[#FFD700] rounded-sm flex items-center justify-center">
              <Crown size={20} weight="fill" className="text-[#FFD700] absolute -top-1" />
              <span className="font-heading text-2xl font-black text-[#FFD700] mt-2">10</span>
            </div>
            <div className="flex flex-col">
              <span className="font-heading text-xl font-black tracking-tighter text-[#FFD700] leading-none">The Kings Head</span>
              <span className="font-heading text-xs font-bold tracking-wider text-text-secondary leading-none">PREDICTIONS</span>
            </div>
          </Link>

          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              if (item.comingSoon) {
                return (
                  <div
                    key={item.path}
                    data-testid={`nav-${item.path.substring(1)}`}
                    className="relative flex items-center space-x-2 px-4 py-2 rounded-sm text-text-muted cursor-default opacity-60"
                  >
                    <Icon size={20} weight="regular" />
                    <span className="text-sm uppercase tracking-wide">{item.label}</span>
                    <span className="absolute -top-1 -right-1 text-[9px] font-bold uppercase tracking-wider bg-[#FFD700] text-black px-1.5 py-0.5 rounded-sm leading-none">Soon</span>
                  </div>
                );
              }
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.substring(1)}`}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-sm transition-all ${
                    isActive
                      ? 'bg-primary text-black font-bold'
                      : 'text-text-secondary hover:text-primary hover:bg-white/5'
                  }`}
                >
                  <Icon size={20} weight={isActive ? 'bold' : 'regular'} />
                  <span className="text-sm uppercase tracking-wide">{item.label}</span>
                </Link>
              );
            })}
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className="px-3 py-1.5 text-sm font-mono font-bold uppercase bg-white/10 text-white border border-white/30 hover:border-primary hover:text-primary hover:bg-primary/10 transition-all rounded-sm"
            >
              {i18n.language === 'en' ? 'PT' : 'EN'}
            </button>
            <Link
              to="/profile"
              data-testid="nav-profile"
              className="hidden md:block p-2 text-text-secondary hover:text-primary transition-colors"
            >
              <UserCircle size={28} weight="bold" />
            </Link>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="hidden md:block text-sm uppercase tracking-wide text-text-secondary hover:text-accent transition-colors"
            >
              {t('nav.logout')}
            </button>
            
            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2.5 bg-primary rounded-sm text-black hover:bg-primary-hover transition-colors shadow-lg"
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={24} weight="bold" /> : <List size={24} weight="bold" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/10 bg-black/95">
            <div className="px-4 py-3 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                if (item.comingSoon) {
                  return (
                    <div
                      key={item.path}
                      data-testid={`mobile-nav-${item.path.substring(1)}`}
                      className="relative flex items-center space-x-3 px-4 py-3 rounded-sm text-white/30 cursor-default"
                    >
                      <Icon size={24} weight="regular" />
                      <span className="text-base uppercase tracking-wide">{item.label}</span>
                      <span className="text-[9px] font-bold uppercase tracking-wider bg-[#FFD700] text-black px-1.5 py-0.5 rounded-sm leading-none ml-auto">Soon</span>
                    </div>
                  );
                }
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${item.path.substring(1)}`}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-sm transition-all ${
                      isActive
                        ? 'bg-primary text-black font-bold'
                        : 'text-white/80 hover:text-primary hover:bg-white/5'
                    }`}
                  >
                    <Icon size={24} weight={isActive ? 'bold' : 'regular'} />
                    <span className="text-base uppercase tracking-wide">{item.label}</span>
                  </Link>
                );
              })}
              
              <Link
                to="/profile"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center space-x-3 px-4 py-3 text-white/80 hover:text-primary hover:bg-white/5 rounded-sm transition-all"
              >
                <UserCircle size={24} weight="bold" />
                <span className="text-base uppercase tracking-wide">{t('nav.profile')}</span>
              </Link>
              
              <button
                onClick={() => {
                  logout();
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center space-x-3 px-4 py-3 text-accent hover:bg-white/5 rounded-sm transition-all"
              >
                <span className="text-base uppercase tracking-wide">{t('nav.logout')}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
